import { useMemo, useState } from 'react'
import { partCategories, parts } from '../catalog/part-catalog'
import { completedRobots } from '../catalog/robot-catalog'
import { useStudioStore } from '../store/useStudioStore'
import { Icon } from './Icon'
import { PartVisual } from './PartVisual'

export function PartsLibrary({ showCompletedRobots = true }: { showCompletedRobots?: boolean }) {
  const [query, setQuery] = useState('')
  const selectedPartId = useStudioStore((state) => state.selectedPartId)
  const selectPart = useStudioStore((state) => state.selectPart)
  const addSelectedPart = useStudioStore((state) => state.addSelectedPart)
  const loadBattlebot = useStudioStore((state) => state.loadBattlebot)
  const loadBattlebotMini = useStudioStore((state) => state.loadBattlebotMini)
  const normalizedQuery = query.trim().toLocaleLowerCase('ko')
  const filteredParts = useMemo(() => {
    return parts.filter((part) => {
      return !normalizedQuery
        || part.name.toLocaleLowerCase('ko').includes(normalizedQuery)
        || part.subtitle.toLocaleLowerCase('ko').includes(normalizedQuery)
    })
  }, [normalizedQuery])
  const filteredRobots = useMemo(() => showCompletedRobots ? completedRobots.filter((robot) => (
    !normalizedQuery
    || robot.name.toLocaleLowerCase('ko').includes(normalizedQuery)
    || robot.id.includes(normalizedQuery)
  )) : [], [normalizedQuery, showCompletedRobots])

  const partsForCategory = (category: (typeof partCategories)[number]) => filteredParts.filter((part) => (
    'series' in category
      ? part.series === category.series
      : 'seriesPrefix' in category
        ? part.series?.startsWith(category.seriesPrefix)
        : false
  ))

  return (
    <aside className="parts-panel">
      <div className="panel-heading">
        <div><strong>부품 라이브러리</strong></div>
      </div>
      <label className="search">
        <Icon name="search" size={15} />
        <input aria-label="라이브러리 검색" placeholder={showCompletedRobots ? '부품 · 로봇 검색' : '미션 부품 검색'} value={query} onChange={(event) => setQuery(event.target.value)} />
        <kbd>⌘ K</kbd>
      </label>

      <div className="library-accordions system-library" aria-label="부품 분류">
        <details className="system-group" open>
          <summary className="system-summary"><span>핑퐁</span><small>{parts.length}종</small></summary>
          <div className="system-categories">
            {filteredRobots.length > 0 && (
              <details className="completed-robots" open={normalizedQuery ? true : undefined}>
                <summary>
                  <span>완성 로봇</span>
                  <small>{filteredRobots.length}</small>
                </summary>
                <div className="robot-presets">
                  {filteredRobots.map((robot) => {
                    const isAvailable = Boolean(robot.assemblyPreset)
                    const loadRobot = robot.assemblyPreset === 'battlebot'
                      ? loadBattlebot
                      : robot.assemblyPreset === 'battlebot-mini'
                        ? loadBattlebotMini
                        : undefined
                    const displayName = isAvailable ? robot.name : `${robot.name} (개발 중)`
                    const assemblySummary = robot.assemblyPreset === 'battlebot'
                      ? '3큐브 · 2증속기 · 2휠'
                      : robot.assemblyPreset === 'battlebot-mini'
                        ? '3큐브 · 2휠 직결'
                        : '조립 데이터 준비 중'
                    return (
                      <button
                        aria-label={isAvailable ? `완성 로봇 ${robot.name} 불러오기` : `완성 로봇 ${displayName}`}
                        className={isAvailable ? 'available' : undefined}
                        disabled={!isAvailable}
                        key={robot.id}
                        onClick={loadRobot}
                        title={isAvailable ? `${robot.name} 조립 불러오기` : '조립 데이터 준비 중'}
                      >
                        <span className="robot-preset-image" aria-hidden="true">
                          <img src={robot.imageUrl} alt="" loading="lazy" decoding="async" />
                        </span>
                        <strong>{displayName}</strong>
                        <small>{assemblySummary}</small>
                        <em>{isAvailable ? '바로 불러오기' : '준비 중'}</em>
                      </button>
                    )
                  })}
                </div>
              </details>
            )}
            {partCategories.map((category, categoryIndex) => {
              const categoryParts = partsForCategory(category)
              if (query && !categoryParts.length) return null
              return (
                <details key={category.id} open={Boolean(query) || categoryIndex === 0}>
                  <summary>
                    <span>{category.name}</span>
                    <small>{categoryParts.length}</small>
                  </summary>
                  <div className="parts-grid">
                    {categoryParts.map((part) => (
                      <button
                        className={selectedPartId === part.id ? 'selected' : ''}
                        draggable
                        key={part.id}
                        onDragStart={(event) => {
                          event.dataTransfer.effectAllowed = 'copy'
                          event.dataTransfer.setData('application/x-pingpong-part', part.id)
                          event.dataTransfer.setData('text/plain', part.name)
                        }}
                        onClick={() => selectPart(part.id)}
                        onDoubleClick={() => {
                          selectPart(part.id)
                          addSelectedPart()
                        }}
                      >
                        <PartVisual part={part} />
                        <strong>{part.name}</strong>
                        <small>{part.subtitle}</small>
                      </button>
                    ))}
                  </div>
                </details>
              )
            })}
          </div>
        </details>
        {[
          { name: '포디프레임', samples: ['연결봉 샘플', '커넥터 샘플'] },
          { name: '레고', samples: ['테크닉 빔 샘플', '핀 커넥터 샘플'] },
        ].map((system) => (
          <details className="system-group" key={system.name}>
            <summary className="system-summary"><span>{system.name}</span><small>준비 중</small></summary>
            <div className="sample-parts">
              {system.samples.map((sample) => <div key={sample}><span aria-hidden="true" /><strong>{sample}</strong><small>테스트 항목</small></div>)}
            </div>
          </details>
        ))}
        {!filteredParts.length && !filteredRobots.length && <p className="empty-parts">검색 결과가 없습니다.</p>}
      </div>

      <p className="drag-hint">섹션을 열고 부품을 작업공간으로 드래그하세요.<br />더블 클릭으로도 바로 추가할 수 있습니다.</p>
    </aside>
  )
}
